import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Gauge, Menu, Moon, Sun, X } from 'lucide-react';

import { useDashboardTheme } from '@/components/dashboard/useDashboardTheme';
import { SITE_LANGS, useLocale } from '@/lib/i18n';

const NAV_SECTIONS = ['features', 'how', 'pricing', 'faq'] as const;

const focus =
  'rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#9a3412]';

function marketingLinks(translate: (key: string) => string) {
  return [
    { href: '#features', id: 'features', label: translate('landing.nav.features') },
    { href: '#how', id: 'how', label: translate('landing.nav.how') },
    { href: '#pricing', id: 'pricing', label: translate('landing.nav.pricing') },
    { href: '#faq', id: 'faq', label: translate('landing.nav.faq') },
  ];
}

function PlatformLinks() {
  const { t } = useLocale();
  return (
    <div className="flex items-center gap-1.5">
      <Link to="/login?callbackUrl=/super-admin" className={`whitespace-nowrap px-2 text-xs font-semibold text-[#f4f1eb] hover:text-white ${focus}`}>
        {t('landing.nav.login')}
      </Link>
      <a href="/#demo" className={`whitespace-nowrap rounded-full bg-[#FF8A00] px-3 py-1.5 text-xs font-semibold text-stone-950 hover:bg-[#ea7900] ${focus}`}>
        {t('landing.nav.demo')}
      </a>
    </div>
  );
}

function useActiveSection(enabled: boolean): string {
  const [current, setCurrent] = useState('');
  useEffect(() => {
    if (!enabled) return;
    const marker = 80;
    function markSection() {
      let active = '';
      NAV_SECTIONS.forEach((id) => {
        const node = document.getElementById(id);
        if (node && node.getBoundingClientRect().top <= marker) active = id;
      });
      setCurrent(active);
    }
    markSection();
    window.addEventListener('scroll', markSection, { passive: true });
    return () => window.removeEventListener('scroll', markSection);
  }, [enabled]);
  return current;
}

const THEME_LABEL: Record<'light' | 'dark', Record<string, string>> = {
  light: { fr: 'Mode clair', en: 'Light mode', ar: 'الوضع الفاتح', es: 'Modo claro' },
  dark: { fr: 'Mode sombre', en: 'Dark mode', ar: 'الوضع الداكن', es: 'Modo oscuro' },
};

/** Marketing home keeps the section links, sign-in, and demo. Agency login and signing keep language and theme. The agency website and the consoles hide the bar. */
export type UtilityBarMode = 'marketing' | 'quiet' | 'hidden';

function agencyKeepsUtilityBar(pathname: string): boolean {
  return pathname === '/login'
    || pathname.startsWith('/sign/')
    || pathname.startsWith('/invite/')
    || pathname.startsWith('/confirm-email/');
}

export function utilityBarForPath(pathname: string, platform: boolean): UtilityBarMode {
  if (pathname === '/admin' || pathname.startsWith('/admin/') || pathname.startsWith('/super-admin')) {
    return 'hidden';
  }
  if (!platform) return agencyKeepsUtilityBar(pathname) ? 'quiet' : 'hidden';
  if (pathname === '/') return 'marketing';
  return 'quiet';
}

export function SiteUtilityBar({
  mode,
  name,
  logoUrl,
}: {
  mode: Exclude<UtilityBarMode, 'hidden'>;
  name: string;
  logoUrl: string;
}) {
  const { lang, setLang, t } = useLocale();
  const { theme, toggleTheme } = useDashboardTheme();
  const [open, setOpen] = useState(false);
  const current = useActiveSection(mode === 'marketing');
  const label = mode === 'marketing' ? 'fleetava' : name.trim() || 'fleetava';
  const nextTheme = theme === 'dark' ? 'light' : 'dark';
  const links = marketingLinks(t);

  return (
    <div className="site-utility fixed inset-x-0 top-0 z-[80] border-b border-white/10 bg-[#161411] text-[#f4f1eb]">
      <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-3 sm:px-6">
        <Link to="/" className={`inline-flex min-w-0 items-center gap-2 ${focus}`}>
          {logoUrl ? (
            <img src={logoUrl} alt="" className="h-7 w-7 rounded object-contain" />
          ) : (
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#FF8A00] text-stone-950">
              <Gauge size={15} aria-hidden />
            </span>
          )}
          <span className="truncate text-sm font-semibold tracking-tight text-[#f4f1eb]">{label}</span>
        </Link>
        {mode === 'marketing' && (
          <nav className="hidden min-w-0 items-center gap-1 lg:flex" aria-label="Marketing">
            {links.map((link) => {
              const active = current === link.id;
              return (
                <a
                  key={link.href}
                  href={link.href}
                  aria-current={active ? 'true' : undefined}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium ${focus} ${
                    active ? 'bg-white/15 text-white' : 'text-[#d6d3d1] hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {link.label}
                </a>
              );
            })}
          </nav>
        )}
        <div className="ms-auto flex min-w-0 items-center gap-1.5 sm:gap-2">
          {mode === 'marketing' ? <PlatformLinks /> : null}
          <div className="inline-flex items-center rounded-full bg-white/10 p-0.5" role="group" aria-label={lang === 'ar' ? 'اللغة' : lang === 'en' ? 'Language' : 'Langue'}>
            {SITE_LANGS.map((entry) => (
              <button
                key={entry.code}
                type="button"
                aria-pressed={lang === entry.code}
                onClick={() => setLang(entry.code)}
                className={`rounded-full px-2 py-1 text-[11px] font-semibold ${
                  lang === entry.code ? 'bg-[#FF8A00] text-stone-950' : 'text-[#d6d3d1] hover:text-white'
                }`}
              >
                {entry.short}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={THEME_LABEL[nextTheme][lang] ?? THEME_LABEL[nextTheme].en}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#d6d3d1] hover:bg-white/10 hover:text-white"
          >
            {theme === 'dark' ? <Sun size={15} aria-hidden /> : <Moon size={15} aria-hidden />}
          </button>
          {mode === 'marketing' && (
            <button
              type="button"
              aria-expanded={open}
              aria-controls="marketing-menu"
              onClick={() => setOpen((value) => !value)}
              className={`rounded-lg border border-white/15 p-1.5 text-[#f4f1eb] lg:hidden ${focus}`}
            >
              {open ? <X size={18} /> : <Menu size={18} />}
              <span className="sr-only">Menu</span>
            </button>
          )}
        </div>
      </div>
      {mode === 'marketing' && open && (
        <div id="marketing-menu" className="border-t border-white/10 bg-[#161411] px-3 py-2 lg:hidden">
          <div className="mx-auto flex max-w-7xl flex-col gap-1">
            {links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                className={`rounded-xl px-3 py-2.5 text-sm font-medium ${current === link.id ? 'bg-white/15 text-white' : 'text-[#f4f1eb]'}`}
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
